<template>
  <div v-title="pageTitle"
       v-loading="loading"
       class="intern-exam-detail">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/training/intern-examination' }">实习生考试管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: listPath }">实习生答卷记录</el-breadcrumb-item>
        <el-breadcrumb-item>答卷详情</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <training-instruction-sub-heading :title="pageTitle"
                                      :entity-id="$route.params.recordId"
                                      :fallback-path="listPath" />

    <div v-if="loadError" class="intern-exam-detail__empty">
      <p>{{ loadError }}</p>
      <el-button type="primary" @click="loadDetail">重新加载</el-button>
      <el-button @click="goBack">返回答卷列表</el-button>
    </div>

    <template v-else-if="detail.record_id">
      <section class="intern-exam-detail__card">
        <div class="intern-exam-detail__card-header">答卷信息</div>
        <div class="intern-exam-detail__card-body">
          <div class="intern-exam-detail__info-grid">
            <div v-for="block in infoBlocks" :key="block.title" class="intern-exam-detail__info-block">
              <h4 class="intern-exam-detail__section-title"><i :class="block.icon" />{{ block.title }}</h4>
              <div v-for="row in block.rows" :key="row.label" class="intern-exam-detail__info-row">
                <span class="intern-exam-detail__info-label">{{ row.label }}</span>
                <span class="intern-exam-detail__info-value">{{ row.value || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="intern-exam-detail__divider" />
          <h4 class="intern-exam-detail__section-title"><i class="el-icon-data-analysis" />得分情况</h4>
          <div class="intern-exam-detail__score-grid">
            <div v-for="card in scoreCards" :key="card.label" :class="['intern-exam-detail__score-card', card.type]">
              <div class="intern-exam-detail__score-label">{{ card.label }}</div>
              <div :class="['intern-exam-detail__score-value', { 'is-text': card.label === '状态' }]">{{ card.value }}</div>
            </div>
          </div>
          <div class="intern-exam-detail__stat-grid">
            <div v-for="card in statCards" :key="card.label" class="intern-exam-detail__stat-card">
              <div class="intern-exam-detail__stat-label">{{ card.label }}</div>
              <div :class="['intern-exam-detail__score-value', card.type]">{{ card.value }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="intern-exam-detail__card">
        <div class="intern-exam-detail__card-header">答题详情（{{ answers.length }} 题）</div>
        <div class="intern-exam-detail__card-body">
          <div v-if="!answers.length" class="intern-exam-detail__empty">暂无答题记录</div>
          <article v-for="(item, index) in answers" :key="item.id" class="intern-exam-detail__question">
            <div class="intern-exam-detail__question-head">
              <span class="intern-exam-detail__question-index">{{ index + 1 }}</span>
              <div class="intern-exam-detail__question-content">
                <div class="intern-exam-detail__question-meta">
                  <span :class="['intern-exam-detail__question-type', questionTypeClass(item)]">{{ item.question_type_name || '题目' }}</span>
                  <span>{{ item.max_score }} 分</span>
                </div>
                <p class="intern-exam-detail__question-title">{{ item.title }}</p>
                <training-media-preview :files="item.sample_files" />
              </div>
              <div :class="['intern-exam-detail__result', answerClass(item)]">
                <template v-if="isGraded(item)">
                  <i :class="item.is_correct ? 'el-icon-check' : 'el-icon-close'" />
                  {{ item.score > 0 ? '+' : '' }}{{ item.score || 0 }}分
                </template>
                <span v-else>未判分</span>
              </div>
            </div>

            <div v-if="(item.options || []).length" class="intern-exam-detail__options">
              <div v-for="option in item.options"
                   :key="option.uuid || option.key"
                   :class="['intern-exam-detail__option', optionClass(item, option)]">
                <span class="intern-exam-detail__option-key">{{ option.key }}</span>
                <span class="intern-exam-detail__option-title">{{ option.title }}</span>
                <i v-if="optionClass(item, option)" :class="optionClass(item, option) === 'is-correct' ? 'el-icon-check' : 'el-icon-close'" />
              </div>
            </div>

            <div class="intern-exam-detail__answer-block">
              <div class="intern-exam-detail__answer-row">
                <span class="intern-exam-detail__answer-label">考生答案</span>
                <div :class="['intern-exam-detail__answer-box', answerClass(item)]">{{ item.user_answer_text || '未作答' }}</div>
              </div>
              <div class="intern-exam-detail__answer-row">
                <span class="intern-exam-detail__answer-label">正确答案</span>
                <div class="intern-exam-detail__answer-box is-standard">{{ item.standard_answer_text || '-' }}</div>
              </div>
              <div v-if="item.analysis_text || (item.analysis_files || []).length" class="intern-exam-detail__answer-row">
                <span class="intern-exam-detail__answer-label">答案解析</span>
                <div class="intern-exam-detail__answer-box is-analysis">
                  <p v-if="item.analysis_text">{{ item.analysis_text }}</p>
                  <training-media-preview :files="item.analysis_files" />
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <div class="intern-exam-detail__footer"><el-button @click="goBack">返回答卷列表</el-button></div>
    </template>
  </div>
</template>

<script>
import { consts } from '@views/training/training-exam-intern.constants'
import TrainingMediaPreview from '@views/training/components/TrainingMediaPreview.vue'
import TrainingInstructionSubHeading from '@views/training/components/TrainingInstructionSubHeading.vue'

export default {
  name: 'TrainingExaminationInternRecordDetail',
  components: { TrainingMediaPreview, TrainingInstructionSubHeading },
  data () {
    return {
      loading: false,
      loadError: '',
      detail: { answer_list: [] },
      loadSequence: 0,
    }
  },
  computed: {
    pageTitle () {
      return this.detail.user_name ? `答卷详情 · ${this.detail.user_name}` : '答卷详情'
    },
    listPath () {
      return `/training/examination/${this.$route.params.examinationId}/intern/records`
    },
    answers () {
      return this.detail.answer_list || []
    },
    infoBlocks () {
      return [
        {
          title: '考生信息',
          icon: 'el-icon-user',
          rows: [
            { label: '姓名', value: this.detail.user_name },
            { label: '用户ID', value: this.detail.user_id },
          ],
        },
        {
          title: '考试信息',
          icon: 'el-icon-document',
          rows: [
            { label: '考试名称', value: this.detail.title },
            { label: '考试ID', value: this.detail.exam_id },
            { label: '提交时间', value: this.detail.submitted_at },
            { label: '答题用时', value: this.detail.duration_text },
          ],
        },
      ]
    },
    scoreCards () {
      const status = Number(this.detail.status)
      const statusType = status === consts.RECORD_STATUS_PASS ? 'is-pass' : (status === consts.RECORD_STATUS_UNPASS ? 'is-fail' : '')
      return [
        { label: '状态', value: this.detail.status_name || '-', type: statusType },
        { label: '总分', value: this.detail.full_score || 0, type: 'is-total' },
        { label: '得分', value: this.detail.score || 0, type: 'is-pass' },
        { label: '及格分', value: this.detail.passing_score || 0, type: 'is-passing' },
        { label: '正确率', value: this.detail.correct_rate_text || '0%', type: 'is-rate' },
      ]
    },
    statCards () {
      return [
        { label: '总题数', value: this.detail.question_count || 0, type: 'is-blue' },
        { label: '答对题数', value: this.detail.correct_count || 0, type: 'is-green' },
        { label: '答错题数', value: this.detail.wrong_count || 0, type: 'is-red' },
      ]
    },
  },
  watch: {
    '$route.params': 'loadDetail',
  },
  created () {
    this.loadDetail()
  },
  methods: {
    loadDetail () {
      const sequence = ++this.loadSequence
      const { recordId, examinationId } = this.$route.params
      this.loading = true
      this.loadError = ''
      this.detail = { answer_list: [] }
      return request.common('getTrainingExaminationInternRecord', { id: recordId }).then(({ data }) => {
        if (sequence !== this.loadSequence) return
        if (!data || Number(data.record_id) !== Number(recordId) || Number(data.exam_id) !== Number(examinationId)) {
          this.loadError = '答卷与当前考试不匹配'
          return
        }
        this.detail = data
      }).catch(() => {
        if (sequence === this.loadSequence) this.loadError = '答卷详情加载失败'
      }).finally(() => {
        if (sequence === this.loadSequence) this.loading = false
      })
    },
    goBack () {
      this.$router.push(this.listPath)
    },
    isGraded (item) {
      return item.is_correct === true || item.is_correct === false
    },
    answerClass (item) {
      if (!this.isGraded(item)) return ''
      return item.is_correct ? 'is-correct' : 'is-wrong'
    },
    optionClass (item, option) {
      if (!this.isGraded(item)) return ''
      const keys = String(item.user_answer_text || '').split('、').map(key => key.trim())
      if (!keys.includes(option.key)) return ''
      const standard = String(item.standard_answer_text || '').split('、').map(key => key.trim())
      return standard.includes(option.key) ? 'is-correct' : 'is-wrong'
    },
    questionTypeClass (item) {
      const types = { single_select: 'is-single', multi_select: 'is-multi', fill_blank: 'is-fill', 1: 'is-single', 2: 'is-multi', 3: 'is-fill' }
      return types[item.question_type] || ''
    },
  },
}
</script>

<style lang="scss" scoped>
.intern-exam-detail {
  min-height: 240px;
  color: #303133;

  &__card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    overflow: hidden;
    & + & { margin-top: 16px; }
  }
  &__card-header { padding: 12px 24px; border-bottom: 1px solid #ebeef5; color: #606266; font-size: 14px; font-weight: 500; }
  &__card-body { padding: 20px 24px; }
  &__info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
  &__info-block { padding: 16px 18px; border: 1px solid #ebeef5; border-radius: 8px; background: #fafbfc; }
  &__section-title {
    display: flex; align-items: center; gap: 8px; margin: 0 0 14px; font-size: 15px; font-weight: 600;
    i { color: #409eff; font-size: 16px; }
  }
  &__info-row { display: flex; align-items: flex-start; gap: 12px; margin-top: 10px; font-size: 14px; line-height: 22px; }
  &__info-label { width: 72px; flex-shrink: 0; color: #909399; }
  &__info-value { flex: 1; min-width: 0; word-break: break-word; }
  &__info-row:first-of-type &__info-value { font-weight: 600; }
  &__divider { height: 1px; margin: 20px 0; background: #ebeef5; }
  &__score-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; }
  &__score-card {
    padding: 14px 16px; min-height: 78px; border-radius: 8px; background: #f5f7fa; box-sizing: border-box;
    &.is-pass { background: linear-gradient(135deg, #ecfdf5, #d1fae5); color: #047857; }
    &.is-fail { background: linear-gradient(135deg, #fef2f2, #fee2e2); color: #dc2626; }
    &.is-total { background: linear-gradient(135deg, #eff6ff, #dbeafe); color: #1d4ed8; }
    &.is-passing { background: linear-gradient(135deg, #fff7ed, #ffedd5); color: #c2410c; }
    &.is-rate { background: linear-gradient(135deg, #f5f3ff, #ede9fe); color: #6d28d9; }
  }
  &__score-label { margin-bottom: 8px; font-size: 13px; opacity: .9; }
  &__score-value { font-size: 24px; font-weight: 600; line-height: 1.2; &.is-text { font-size: 18px; line-height: 1.35; } }
  &__stat-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
  &__stat-card { padding: 14px 16px; border: 1px solid #ebeef5; border-radius: 8px; }
  &__stat-label { margin-bottom: 8px; color: #909399; font-size: 13px; }
  .is-blue { color: #409eff; }
  .is-green { color: #67c23a; }
  .is-red { color: #f56c6c; }
  &__empty { padding: 32px 0; text-align: center; color: #909399; font-size: 14px; }
  &__question {
    padding: 20px 0; border-bottom: 1px solid #ebeef5;
    &:first-child { padding-top: 4px; }
    &:last-child { padding-bottom: 4px; border-bottom: none; }
  }
  &__question-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
  &__question-index { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%; background: #f0f2f5; color: #606266; font-size: 14px; font-weight: 600; }
  &__question-content { flex: 1; min-width: 0; }
  &__question-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; color: #909399; font-size: 13px; }
  &__question-type {
    display: inline-block; padding: 2px 10px; border-radius: 999px; background: #f3f4f6; font-size: 12px; line-height: 20px;
    &.is-single { background: #dbeafe; color: #1d4ed8; }
    &.is-multi { background: #ede9fe; color: #6d28d9; }
    &.is-fill { background: #dcfce7; color: #15803d; }
  }
  &__question-title { margin: 0; font-size: 15px; line-height: 1.65; white-space: pre-wrap; word-break: break-word; }
  &__result {
    display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; padding: 8px 14px; border-radius: 8px; background: #f5f7fa; color: #909399; font-size: 14px; font-weight: 600; white-space: nowrap;
    &.is-correct { background: #ecfdf5; color: #047857; }
    &.is-wrong { background: #fef2f2; color: #dc2626; }
  }
  &__options { display: flex; flex-direction: column; gap: 8px; margin: 0 0 14px 44px; }
  &__option {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid transparent; border-radius: 8px; background: #f5f7fa; font-size: 14px; line-height: 24px;
    &.is-correct { border-color: #bbf7d0; background: #ecfdf5; i { color: #047857; } }
    &.is-wrong { border-color: #fecaca; background: #fef2f2; i { color: #dc2626; } }
  }
  &__option-key { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 24px; height: 24px; border: 1px solid #dcdfe6; border-radius: 50%; background: #fff; color: #606266; font-size: 12px; }
  &__option-title { flex: 1; min-width: 0; color: #606266; word-break: break-word; }
  &__answer-block { display: flex; flex-direction: column; gap: 10px; margin-left: 44px; }
  &__answer-row { display: flex; align-items: flex-start; gap: 12px; }
  &__answer-label { width: 72px; flex-shrink: 0; padding-top: 8px; color: #909399; font-size: 14px; line-height: 22px; }
  &__answer-box {
    flex: 1; min-width: 0; padding: 8px 14px; border: 1px solid #ebeef5; border-radius: 8px; background: #f5f7fa; color: #606266; font-size: 14px; line-height: 1.65; white-space: pre-wrap; word-break: break-word;
    &.is-correct { border-color: #bbf7d0; background: #ecfdf5; color: #047857; }
    &.is-wrong { border-color: #fecaca; background: #fef2f2; color: #dc2626; }
    &.is-standard { border-color: #bfdbfe; background: #eff6ff; color: #1d4ed8; }
    &.is-analysis { border-color: #fde68a; background: #fffbeb; color: #92400e; p { margin: 0; } }
  }
  &__footer { margin: 16px 0 8px; text-align: center; }
}

@media (max-width: 1200px) {
  .intern-exam-detail__score-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 992px) {
  .intern-exam-detail {
    &__info-grid { grid-template-columns: 1fr; }
    &__score-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
}
@media (max-width: 768px) {
  .intern-exam-detail {
    &__card-header, &__card-body { padding: 16px; }
    &__score-grid, &__stat-grid { grid-template-columns: 1fr; }
    &__question-head { flex-wrap: wrap; }
    &__result { margin-left: 44px; }
    &__options, &__answer-block { margin-left: 0; }
    &__answer-label { width: 56px; }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/exam/TrainingExaminationInternRecordDetail.vue